import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestUtilizationEfficiencyReportsInterFace,
  UtilizationEfficiencyReportsInterFace,
} from '../../../../interface/utilization-efficiency-report-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  disableUtilizationEfficiencyReportsFiltersApplied,
  fetchUtilizationEfficiencyReports,
  fetchListUtilizationEfficiencyReportsFilters,
  updateUtilizationEfficiencyReportsFilters,
  setSortData,
} from '../../../../redux/slices/UtilizationEfficiencyReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import stationManagement from '../../../../utils/api/station-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}
/**
 * UtilizationEfficiencyReport
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function UtilizationEfficiencyReport(properties: ReportLeftTopContent) {
  const { t } = useTranslation('sidePanel');
  const { leftTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    UtilizationEfficiencyReports,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.UtilizationEfficiencyReport);

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const column = [
    {
      label: t('deviceSnShort'),
      accessorKey: 'chargingPointSn' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('utilizationPercentage'),
      accessorKey: 'utilizationPercentage' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('idleTimeRatio'),
      accessorKey: 'idleTime' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('peakVsOffPeakSessionRatio'),
      accessorKey: 'offPeakSessions' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * peakSessions
       * @param {any} UtilizationEfficiency - The UtilizationEfficiency
       */
      customData: (UtilizationEfficiency: UtilizationEfficiencyReportsInterFace) => (
        <p>
          {UtilizationEfficiency.peakSessions}-{UtilizationEfficiency.offPeakSessions}
        </p>
      ),
    },
    {
      label: t('sessionsPerConnector'),
      accessorKey: 'totalSessionsPerConnector' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('revenuePerKwh'),
      accessorKey: 'totalRevenuePerConnector' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: RequestUtilizationEfficiencyReportsInterFace) => {
    if (!loading && selectedChargingPoint?.id) {
      dispatch(
        fetchUtilizationEfficiencyReports({ ...data, chargingStation: selectedChargingPoint.id }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('deviceSnShort'), accessorKey: 'chargingPointSn' },
    { label: t('utilizationPercentage'), accessorKey: 'utilizationPercentage' },
    { label: t('idleTimeRatio'), accessorKey: 'idleTime' },
    { label: t('peakVsOffPeakSessionRatio'), accessorKey: '__peakOffPeak__' },
    { label: t('sessionsPerConnector'), accessorKey: 'totalSessionsPerConnector' },
    { label: t('revenuePerKwh'), accessorKey: 'totalRevenuePerConnector' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagement
      .fetchReportUtilizationExport({
        ...queryParameters,
        chargingStation: selectedChargingPoint?.id,
        sortBy,
        sortField,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map(
          (row: UtilizationEfficiencyReportsInterFace, index: number) =>
            exportCurrentColumns.map((col) => {
              switch (col.accessorKey) {
                case '__serial__':
                  return index + 1;
                case 'created_at':
                  return ConvertUTCtoDate(row?.created_at) || 'N/A';
                case 'time':
                  return ConvertUTCtoHHMM(row?.created_at) || t('unavailable');
                default:
                  return (row as any)[col?.accessorKey] || t('unavailable');
              }
            }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableUtilizationEfficiencyReportsFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('UtilizationEfficiencyReportsFilters'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 2) {
      dispatch(fetchListUtilizationEfficiencyReportsFilters({ chargingStation: currentId }));
    }
    if (UtilizationEfficiencyReports.length === 0 || lastId !== currentId) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableUtilizationEfficiencyReportsFiltersApplied());
    }
    sessionStorage.setItem('UtilizationEfficiencyReportsFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);
  return (
    <div>
      <CustomTable
        version="v2"
        header={column}
        data={UtilizationEfficiencyReports}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateUtilizationEfficiencyReportsFilters}
        filterListToDispatch={fetchListUtilizationEfficiencyReportsFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        leftTopContent={leftTopContent}
        pageLimitChange={pageNateLimitChange}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        height={400}
        disableHeader={false}
        rightTopContent={<> </>}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default UtilizationEfficiencyReport;
